import { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { IPageState, PageContext } from '../../contexts/PageContext';
import { BUY_SELL_STEPS, CHARGE_TYPES, CURRENCIES, PATHS, TRANSACTION_STATUS, TRANSACTION_TYPES, WEIGHT_UNITS } from '../../utils/constant';
import Inbound from '../$Inbound';
import Delivery from '../$Delivery';
import PhysicalOrder from '../$PhysicalOrder';
import { formatDate, getFormattedCurrency, getThreePointNumber, getTwoPointNumber, objToArray } from '../../utils/helpers';
import Billing from '../$Billing';
import Summary from '../$Summary';
import SelectCustomer from '../$SelectCustomer';
import { TransactionContext } from '../../contexts/TransactionContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TTransactionBasic } from '../../Models/Transactions';
import useService from '../../hooks/useService';
import { getLotDetails, getTransaction as getTransactionService } from '../../services/Transactions';
import { CustomerContext } from '../../contexts/CustomerContext';
import HighlightsCardAndCustomerDetails from '../custom/HighlightsCardAndCustomerDetails';

export type TBuySellStpeKeys = 'INBOUND' | 'PHYSICAL_ORDER' | 'DELIVERY' | 'BILLING' | 'SUMMARY'

const PAGE_INFO: IPageState = {
    title: 'Buy Sell',
    breadcrumbs: objToArray(BUY_SELL_STEPS).map(item => ({ path: item.key, label: item.value })),
    currentPage: BUY_SELL_STEPS.INBOUND,
    path: PATHS.BUY_SELL
}

type TBuysellSummaryKeys = 'customerId' 
| 'customerName' 
| 'pvcName' 
| 'transactionDate' 
| 'inboundMetal' 
| 'purchasedMetal' 
| 'deliveryCharge'
| 'totalWBMGold'
| 'totalWAMGold'
| 'totalWAMTGold'
| 'settlementFineWeightGold'
| 'totalTaxValue'
| 'totalCharges'
| 'roundingRs'
| 'totalValue'
| 'totalMetalReturnNetWeightGold'

const SUMMARY_DETAILS: {id: TBuysellSummaryKeys, label: any, value: any}[] = [
    {
        id: 'customerId',
        label:"Customer Id",
        value : ""
    },
    {
        id:"customerName",
        label:"Customer Name",
        value : ""
    },
    {
        id:"pvcName",
        label:"PVC Name",
        value : ""
    },
    {
        id:"transactionDate",
        label:"Transaction Date",
        value : ""
    },
    {
        id:"inboundMetal",
        label:"Inbound Metal",
        value : ""
    },
    {
        id:"purchasedMetal",
        label:"Purchased Metal",
        value : ""
    },
    {
        id:'deliveryCharge',
        label:"Delivery Charges",
        value : ''
    },
    {
        id:'totalWBMGold',
        label:"Total WBM",
        value : ''
    },
    {
        id:'totalWAMGold',
        label:"Total WAM",
        value : ''
    },
    {
        id:'totalWAMTGold',
        label:"Total WAMT",
        value : ''
    },
    {
        id:'settlementFineWeightGold',
        label:"Settlement Fine Wt. Gold",
        value : ''
    },
    {
        id:'totalMetalReturnNetWeightGold',
        label:"Total Metal Return Net Wt. Gold",
        value : ''
    },
    {
        id:'totalTaxValue',
        label:"Total Tax Value Rs.",
        value : ''
    },
    {
        id:'totalCharges',
        label:"Total Charges Rs.",
        value : ''
    },
    {
        id:'roundingRs',
        label:"Rounding Rs.",
        value : ''
    },
    {
        id:'totalValue',
        label:"Grand Total Rs.",
        value : ''
    },

]

const Container = styled.div`
    width: 100%;
`

const getPageInfo = (section: TBuySellStpeKeys) => {
    const pageInfo = { ...PAGE_INFO, currentPage: section || 'INBOUND' }
    return pageInfo
}


const BuySell = () => {

    const { setPageState } = useContext(PageContext)
    const { 
        setTransactionInfo, 
        transactionInfo: { transactionType, globalTransactionNumber, status, updatedAt }, 
        inboundDetails, resetOrder, setCapturedSteps, pvcCenterDetails, payments,
        physicalOrder, meltingLot
     } = useContext(TransactionContext)
    const { user } = useContext(AuthContext)
    const { customerConfig, customerAddresses } = useContext(CustomerContext)
    const [searchParams] = useSearchParams()
    const { id } = useParams()
    const { execute } = useService()
    const navigate = useNavigate()

    const step = searchParams.get('section')

    const getTransaction = async () => {
        const { data } = await execute(() => getTransactionService({ globalTransactionNumber: id!, transactionType }))
        if(data?.status === TRANSACTION_STATUS.COMPLETED) await execute(()=> getLotDetails({globalTransactionNumber:data?.globalTransactionNumber!}))
        if (data !== null) {
            switch (data.status) {
                case TRANSACTION_STATUS.INBOUND_INITIATED:
                    navigate(PATHS.BUY_SELL + '/' + id + '?section=INBOUND')
                    break;
                case TRANSACTION_STATUS.PHYSICAL_ORDER_INITIATED:
                    setCapturedSteps(['INBOUND'])
                    navigate(PATHS.BUY_SELL + '/' + id + '?section=PHYSICAL_ORDER')
                    break;
                case TRANSACTION_STATUS.PAYMENT_INITIATED:
                    setCapturedSteps(['INBOUND', 'PHYSICAL_ORDER'])
                    navigate(PATHS.BUY_SELL + '/' + id + '?section=BILLING')
                    break;
                // case TRANSACTION_STATUS.DELIVERY_INITIATED :
                //     setCapturedSteps(['INBOUND', 'PHYSICAL_ORDER', 'BILLING'])
                //     navigate(PATHS.BUY_SELL + '/' + id + '?section=DELIVERY')
                //     break;
                case TRANSACTION_STATUS.DELIVERY_INITIATED :
                case TRANSACTION_STATUS.PENDING_CANCELLATION :
                case TRANSACTION_STATUS.CANCELLED :
                case TRANSACTION_STATUS.COMPLETED:
                    setCapturedSteps(['INBOUND', 'PHYSICAL_ORDER', 'BILLING'])
                    navigate(PATHS.BUY_SELL + '/' + id + '?section=SUMMARY')
                    break;
                default:
                    navigate(PATHS.BUY_SELL + '/' + id + '?section=INBOUND')
                    break;
            }

        }
    }

    const cancelTransactionHandler = (transactionId:string) => {
        navigate(PATHS.PVC_TRANSACTIONS)
    }
    
    useEffect(() => {
        setPageState(getPageInfo(step as TBuySellStpeKeys))
    }, [step])

    useEffect(() => {
        resetOrder()

        if (id) {
            getTransaction()
        }
        else {

            setTransactionInfo({
                currency: CURRENCIES.INR,
                pvcCenterId: user.centerId!,
                status: TRANSACTION_STATUS.INBOUND_INITIATED,
                transactionType: TRANSACTION_TYPES.BUY_SELL,
                weightUOM: WEIGHT_UNITS.GM,
            } as TTransactionBasic)
        }

        return () => resetOrder()

    }, [])
    
    const getSummary = () => {
        let totalWBM = 0
        let totalWAM = 0
        let totalWAMT = 0
        let settlementFineWeightGold = 0
        let totalMetalReturnNetWeightGold = 0
        console.log('in summary...', meltingLot)
        meltingLot.forEach(lot => {
            console.log('lot in summary...')
            totalWBM += lot.netWeight
            totalWAM += lot.weightAfterMelting
            totalWAMT += lot.wamt
            settlementFineWeightGold += lot.fineWeightToReturn
            totalMetalReturnNetWeightGold += lot.finalSettlementNetWeight
        })
        let totalTaxValue = 0
        physicalOrder.taxes?.forEach((tax) => {
            totalTaxValue += tax.taxAmount
        })
        let totalCharges = 0
        physicalOrder.charges?.forEach((charge) => {
            totalCharges += charge.preTaxAmount
            totalTaxValue += charge.postTaxAmount - charge.preTaxAmount
        })
        const roundingRs = getTwoPointNumber((Math.round(physicalOrder.totalPrice || 0) - (physicalOrder.totalPrice || 0)) ?? 0)
        return SUMMARY_DETAILS.map(field => {
            switch (field.id) {
                case 'customerId':
                    field.value = customerConfig.customerRefNo
                    break;
                case 'customerName':
                    field.value = customerConfig.fullName
                    break;
                case 'pvcName':
                    field.value = pvcCenterDetails.name
                    break;
                case 'inboundMetal':
                    field.value = inboundDetails.shipmentWeight + ' gm'
                    break;
                case 'purchasedMetal':
                    field.value = physicalOrder.totalGoldWeight + ' gm'
                    break;
                case 'transactionDate':
                    field.value = updatedAt ? formatDate(updatedAt) : ''
                    break;
                case 'deliveryCharge':
                    const deliveryCharge = physicalOrder.charges?.filter((charge)=>{
                        return charge.chargeId === CHARGE_TYPES.DELIVERY_CHARGE
                    })[0]
                    field.value = deliveryCharge ? getFormattedCurrency(deliveryCharge.postTaxAmount) : null
                    break;
                case 'totalWBMGold':
                    field.value = getThreePointNumber(totalWBM)
                    break;
                case 'totalWAMGold':
                    field.value = getThreePointNumber(totalWAM)
                    break;
                case 'totalWAMTGold':
                    field.value = getThreePointNumber(totalWAMT)
                    break;
                case 'settlementFineWeightGold':
                    field.value = getThreePointNumber(settlementFineWeightGold)
                    break;
                case 'totalMetalReturnNetWeightGold':
                    field.value = getThreePointNumber(totalMetalReturnNetWeightGold)
                    break;
                case 'totalTaxValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalTaxValue))
                    break;
                case 'totalCharges':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalCharges))
                    break;
                case 'roundingRs':
                    field.value = getFormattedCurrency(getTwoPointNumber(roundingRs))
                    break;
                case 'totalValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(physicalOrder.totalPrice || 0))
                    break;
                    
            }
            return field
        })
    }

    useEffect(()=>{
        console.log('inboundDetails', inboundDetails)
    },[inboundDetails])

    const PageContent = useCallback(() => {
        switch (step as TBuySellStpeKeys) {
            case 'INBOUND':
                return <Inbound nextButtonLable='Next (Physical Order)' nextSection='PHYSICAL_ORDER' path={PATHS.BUY_SELL} />

            case 'PHYSICAL_ORDER':
                return <PhysicalOrder path={PATHS.BUY_SELL} nextSection='BILLING' prevSection='INBOUND' nextButtonLable='Next (Billing)' />

            case 'BILLING':
                return <Billing path={PATHS.BUY_SELL} nextSection='SUMMARY' prevSection='PHYSICAL_ORDER' nextButtonLable='Next (Delivery)' />

            // case 'DELIVERY':
            //     return <Delivery path={PATHS.BUY_SELL} nextSection='SUMMARY' prevSection='BILLING' nextButtonLable='Generate Invoice' />

            case 'SUMMARY':
                return <Summary prevSection='BILLING' path={PATHS.BUY_SELL} transitionHistory={[]} summaryDetails={getSummary()} />

            default:
                return <SelectCustomer path={PATHS.BUY_SELL} nextSection='INBOUND' />
        }
    }, [step])


    return (
        <Container>
            { id ? <HighlightsCardAndCustomerDetails
                        customerConfig={customerConfig}
                        globalTransactionNumber={globalTransactionNumber as string}
                        status={status}
                        inboundId={inboundDetails.id}
                        onSuccessCancelTransaction={cancelTransactionHandler}
                        transactionType={transactionType}
                    /> 
                : <></>
             }
            {inboundDetails && customerConfig ? <PageContent /> : <></>}
        </Container>
    )
}


export default BuySell